import * as XLSX from 'xlsx';


// Column widths for a sheet built with XLSX.utils.json_to_sheet.
//
// json_to_sheet writes no !cols, so every column renders at Excel's default ~8.4 characters and
// anything longer is visually clipped — names, emails and event lists all read as truncated even
// though the data is intact. This measures each column's header and values and sets a width that
// fits, which is the whole difference between a usable export and one the reader has to resize.
//
// Widths are clamped: MIN keeps short numeric columns readable, MAX stops a long free-text column
// (an event list, a comment) from pushing everything else off screen.
const MIN_WIDTH = 8;
const MAX_WIDTH = 60;
const PADDING = 2;

export function autoFitColumns(
  worksheet: XLSX.WorkSheet,
  rows: Record<string, unknown>[]
): void {
  if (!rows.length) {
    return;
  }

  // Header order comes from the first row — json_to_sheet uses the same key order.
  const headers = Object.keys(rows[0]);

  worksheet['!cols'] = headers.map(header => {
    let widest = header.length;
    for (const row of rows) {
      const value = row[header];
      if (value === null || value === undefined) {
        continue;
      }
      const len = String(value).length;
      if (len > widest) {
        widest = len;
      }
    }
    return { wch: Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, widest + PADDING)) };
  });
}

// Enable Excel's auto-filter over the populated range, giving every column a filter/sort dropdown.
//
// NOTE: freeze panes are deliberately NOT attempted here. The community build of xlsx (0.18.5)
// silently drops !freeze / !views / !panes — verified by inspecting the generated sheet XML, where
// no <pane> element is written — so a "freeze the header" helper would be dead code that looks like
// it works. Auto-filter IS written, and gives the reader sorting plus a sticky-ish header via
// Excel's own UI.
export function addAutoFilter(worksheet: XLSX.WorkSheet): void {
  if (worksheet['!ref']) {
    worksheet['!autofilter'] = { ref: worksheet['!ref'] };
  }
}
